/**
 * Why the consent screen failed to load a pending authorization request.
 *
 * The screen used to render one "this request has expired" line for every failure, which sent
 * users back to their MCP client to restart a flow that a stale session, a Redis outage or a
 * server error would break just as reliably the second time. Each kind gets its own message, and
 * an unexpected one carries its status code so the next report is diagnosable.
 */
export type ConsentErrorKind = "session" | "expired" | "unavailable" | "network";

export interface ConsentError {
	kind: ConsentErrorKind;
	/** HTTP status, or `null` when the request never reached the API. */
	status: number | null;
}

/** RTK Query reports transport failures with a string `status` (`FETCH_ERROR`, `TIMEOUT_ERROR`). */
function readStatus(error: unknown): number | null {
	if (typeof error !== "object" || error === null || !("status" in error)) {
		return null;
	}

	const { status } = error as { status: unknown };

	return typeof status === "number" ? status : null;
}

export function resolveConsentError(error: unknown): ConsentError {
	const status = readStatus(error);

	if (status === 401 || status === 403) {
		return { kind: "session", status };
	}

	if (status === 404) {
		return { kind: "expired", status };
	}

	return { kind: status === null ? "network" : "unavailable", status };
}

const MESSAGE_KEY: Record<ConsentErrorKind, string> = {
	session: "sessionExpired",
	expired: "expired",
	unavailable: "unavailable",
	network: "networkFailed",
};

export function consentErrorMessageKey(kind: ConsentErrorKind): string {
	return MESSAGE_KEY[kind];
}
